<template>
  <div class="bpf">
    <div class="bpf-url" translate="no">
      <span>369.dev/ws/your-workspace/book</span>
      <em>Preview</em>
    </div>
    <div ref="viewport" class="bpf-viewport" :style="{ height: `${Math.ceil(height * scale)}px` }">
    <iframe
      v-if="src"
      ref="frame"
      class="bpf-frame"
      :class="{ 'bpf-frame-ready': shown }"
      :src="src"
      :style="frameStyle"
      title="Your booking page preview"
      referrerpolicy="no-referrer"
      sandbox="allow-scripts allow-same-origin"
    />
    </div>
    <p v-if="!shown" class="bpf-status" role="status">
      {{ failed ? "The live preview couldn't load here. Your page will look like Workspace369's booking pages." : "Loading your booking page…" }}
    </p>
    <p class="bpf-note">Sample times, prices in US dollars. Your live page uses your days, time zone and currency.</p>
  </div>
</template>

<script setup>
// The real 369.dev booking page in preview mode (369.dev/booking/preview),
// driven over the same postMessage bridge the Workspace369 app uses
// (369.dev utils/publicPreview/bridge.ts):
//   369.dev → us   { source: 'w369-booking-preview', type: 'ready' | 'size', channel, height?, seq }
//   us → 369.dev   { source: 'w369-booking-preview-host', type: 'render', channel, seq, payload }
// 369.dev accepts renders only from an allow-listed origin (workspace369.com,
// www.workspace369.com, localhost) on the channel it was given, and the
// preview never books, saves or calls the booking API.
import { BOOKING_PREVIEW_ORIGIN, bookingPreviewSrc } from "~/utils/booking-page-draft.mjs";

const props = defineProps({
  /** The 369.dev BookingPreviewPayload to show (bookingPreviewPayload). */
  payload: { type: Object, default: null },
});

const PAGE_SOURCE = "w369-booking-preview";
const HOST_SOURCE = "w369-booking-preview-host";
const ALLOWED_HOSTS = ["https://workspace369.com", "https://www.workspace369.com"];
const LOCAL_HOST = /^http:\/\/(?:localhost|127\.0\.0\.1):\d{1,5}$/;
const MIN_HEIGHT = 420;
const MAX_HEIGHT = 1400;

// The booking page needs about 420px to lay out; a narrower column (a phone)
// shows it at that width, scaled down to fit, instead of cutting it off.
const MIN_LAYOUT_WIDTH = 420;

const frame = ref(null);
const viewport = ref(null);
const columnWidth = ref(0);
const layoutWidth = computed(() => Math.max(columnWidth.value, MIN_LAYOUT_WIDTH));
const scale = computed(() => (columnWidth.value > 0 && columnWidth.value < MIN_LAYOUT_WIDTH ? columnWidth.value / MIN_LAYOUT_WIDTH : 1));
const frameStyle = computed(() => ({
  width: columnWidth.value > 0 ? `${layoutWidth.value}px` : "100%",
  height: `${height.value}px`,
  transform: scale.value === 1 ? undefined : `scale(${scale.value})`,
}));
let resizeObserver = null;
const src = ref("");
const ready = ref(false);
const shown = ref(false);
const failed = ref(false);
const height = ref(560);
let channel = "";
let seq = 0;
let timer = 0;
let renderTimer = 0;

function render() {
  window.clearTimeout(renderTimer);
  renderTimer = 0;
  const target = frame.value?.contentWindow;
  if (!ready.value || !target || !props.payload) return;
  seq += 1;
  target.postMessage({ source: HOST_SOURCE, type: "render", channel, seq, payload: toRaw(props.payload) }, BOOKING_PREVIEW_ORIGIN);
}

function onMessage(event) {
  if (event.origin !== BOOKING_PREVIEW_ORIGIN) return;
  const data = event.data;
  if (!data || typeof data !== "object" || data.source !== PAGE_SOURCE || data.channel !== channel) return;
  // Every `ready` gets the current page: a reloaded preview starts over.
  if (data.type === "ready") {
    ready.value = true;
    render();
  }
  if (data.type === "size") {
    const next = Number(data.height);
    if (Number.isFinite(next) && next > 0) height.value = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.ceil(next)));
    // Reveal only once the page shows one of our renders, never the sample.
    if (Number(data.seq) > 0) {
      shown.value = true;
      failed.value = false;
    }
  }
}

watch(() => props.payload, () => {
  window.clearTimeout(renderTimer);
  renderTimer = window.setTimeout(render, 120);
}, { deep: true });

onMounted(() => {
  const origin = window.location.origin;
  if (!ALLOWED_HOSTS.includes(origin) && !LOCAL_HOST.test(origin)) {
    failed.value = true;
    return;
  }
  const measure = () => {
    columnWidth.value = viewport.value?.clientWidth ?? 0;
  };
  measure();
  if (typeof ResizeObserver === "function" && viewport.value) {
    resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(viewport.value);
  }
  const random = Math.random().toString(36).slice(2, 12);
  channel = `w369gen_${random}`;
  window.addEventListener("message", onMessage);
  src.value = bookingPreviewSrc(origin, channel);
  timer = window.setTimeout(() => {
    if (!shown.value) failed.value = true;
  }, 15000);
});

onBeforeUnmount(() => {
  window.removeEventListener("message", onMessage);
  resizeObserver?.disconnect();
  window.clearTimeout(timer);
  window.clearTimeout(renderTimer);
});
</script>

<style scoped>
.bpf {
  scroll-margin-top: 6rem;
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  border-top: 1px solid #e2e8f0;
}
.bpf-url {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 1.25rem;
  font-size: 0.75rem;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
}
.bpf-url span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bpf-url em {
  flex: none;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0e7490;
}
/* The frame is taken out of the flow, so its 420px layout width never widens
   the column it sits in; the viewport's own height follows the page. */
.bpf-viewport {
  position: relative;
  overflow: hidden;
  min-width: 0;
}
.bpf-frame {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  transform-origin: 0 0;
  border: 0;
  background: #fff;
  opacity: 0;
  transition: opacity 0.2s ease;
}
.bpf-frame-ready {
  opacity: 1;
}
.bpf-note {
  margin: 0;
  padding: 0.6rem 1.25rem 0.9rem;
  font-size: 0.8125rem;
  color: #64748b;
  border-top: 1px solid #e2e8f0;
}
.bpf-status {
  position: absolute;
  top: 2.5rem;
  left: 0;
  right: 0;
  margin: 0;
  padding: 1rem 1.25rem 1.5rem;
  font-size: 0.875rem;
  color: #64748b;
}
@media (prefers-reduced-motion: reduce) {
  .bpf-frame {
    transition: none;
  }
}
:global(.dark) .bpf,
:global(.dark) .bpf-url {
  border-color: #243247;
}
:global(.dark) .bpf-url {
  color: #94a3b8;
}
:global(.dark) .bpf-url em {
  color: #67e8f9;
}
:global(.dark) .bpf-note {
  color: #94a3b8;
  border-color: #243247;
}
:global(.dark) .bpf-status {
  color: #94a3b8;
}
</style>
